// Data hooks for the customer-oriented dashboard. Two endpoints land Phase 1:
//   GET /api/projects/:id/dashboard/config  — label→stage mapping + suggestion
//   GET /api/projects/:id/dashboard         — KPIs + lead-time + decisions + summary
//
// Phase 2 + 3 add the mutation hooks (create/edit/stage-move).

import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { DashboardDataSchema, type DashboardData } from "@loom/shared/dashboard";

import { httpGet, UnauthorizedError } from "../lib/http";

const labelArray = z.array(z.string());

const StageMappingSchema = z.object({
  needs_input: labelArray,
  planned: labelArray,
  in_progress: labelArray,
  review: labelArray,
  shipped: labelArray,
});

const StageMappingPartialSchema = StageMappingSchema.partial();

const DashboardConfigResponseSchema = z.object({
  configured: z.boolean(),
  mapping: StageMappingSchema,
  decisionLabels: z.array(z.string()),
  suggestion: z.object({
    mapping: StageMappingPartialSchema,
    decisionLabels: z.array(z.string()),
    chosenScope: z.string().nullable(),
    confidence: z.enum(["high", "partial", "none"]),
  }),
});

export type DashboardConfigResponse = z.infer<typeof DashboardConfigResponseSchema>;

export interface DashboardState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
  /** Re-fetch on demand — used after mutations land. */
  readonly refetch: () => void;
}

const useFetchOnce = <T>(
  path: string,
  schema: z.ZodType<T>,
): DashboardState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [tick, setTick] = useState<number>(0);
  const aborted = useRef(false);

  useEffect(() => {
    aborted.current = false;
    setLoading(true);
    setError(null);
    httpGet(path, schema)
      .then((value) => {
        if (aborted.current) return;
        setData(value);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        if (err instanceof UnauthorizedError) {
          // Route loader will redirect; keep loading so we don't flash an error.
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      aborted.current = true;
    };
  }, [path, schema, tick]);

  return { data, loading, error, refetch: () => setTick((n) => n + 1) };
};

export const useDashboardConfig = (
  projectId: number,
): DashboardState<DashboardConfigResponse> =>
  useFetchOnce(
    `/api/projects/${projectId}/dashboard/config`,
    DashboardConfigResponseSchema,
  );

export const useDashboardData = (
  projectId: number,
): DashboardState<DashboardData> =>
  useFetchOnce(`/api/projects/${projectId}/dashboard`, DashboardDataSchema);

const ProjectLabelsResponseSchema = z.object({
  labels: z.array(z.string()),
});

export type ProjectLabelsResponse = z.infer<typeof ProjectLabelsResponseSchema>;

/**
 * Fetch the full label vocabulary for a GitLab project. Used by the
 * SetupBanner stage-mapping editor as an autocomplete source so the
 * customer picks real labels instead of guessing the canonical spelling.
 * One-shot fetch on mount — the label set is stable enough on the human
 * timescale that polling would be wasteful.
 */
export const useProjectLabels = (
  projectId: number,
): DashboardState<ProjectLabelsResponse> =>
  useFetchOnce(
    `/api/projects/${projectId}/labels`,
    ProjectLabelsResponseSchema,
  );
